export function UnderstandingPropertyValue() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Understanding Your Apartment Building's Value
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Determining the worth of your apartment building is both an art and a science. Unlike single-family homes, apartment buildings are valued primarily as income-producing investments rather than personal residences. Professional investors and appraisers use multiple sophisticated methods to arrive at an accurate market value.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/1bfa8fc5a5326795edb54a6345e87e64.jpg"
              alt="Professional apartment building valuation analysis"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Why Accurate Valuation Matters</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Understanding your apartment building's true market value is essential whether you're selling, refinancing, or simply evaluating your investment portfolio. An accurate valuation helps you make informed decisions, set realistic pricing expectations, and negotiate from a position of knowledge and strength.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The valuation process considers numerous factors including current income, operating expenses, property condition, location desirability, market trends, and comparable sales. Professional investors typically use three primary valuation approaches, each providing unique insights into your property's worth.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-money-dollar-circle-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Income-Based Valuation</h4>
                  <p className="text-gray-600">Most important for investors - focuses on cash flow and returns</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-building-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Market Comparison</h4>
                  <p className="text-gray-600">Analyzes recent sales of similar properties in your area</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-hammer-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Replacement Cost</h4>
                  <p className="text-gray-600">Calculates what it would cost to rebuild the property today</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">The Valuation Process Overview</h3>
          <p className="text-lg text-gray-700 mb-8 text-center max-w-4xl mx-auto leading-relaxed">
            Professional apartment building valuation involves gathering comprehensive financial data, analyzing market conditions, inspecting the physical property, and applying multiple valuation methodologies to arrive at a well-supported market value estimate.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-file-list-3-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Data Collection</h4>
              <p className="text-gray-600 leading-relaxed">
                Gather rent rolls, operating statements, lease agreements, maintenance records, and property tax information for comprehensive analysis.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Market Analysis</h4>
              <p className="text-gray-600 leading-relaxed">
                Research comparable sales, current market cap rates, rental rates, vacancy trends, and economic indicators in your submarket.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-calculator-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Value Calculation</h4>
              <p className="text-gray-600 leading-relaxed">
                Apply income, sales comparison, and cost approaches to determine a final value estimate supported by market evidence.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}